/**
 * ProtectedButton Component
 * Button that automatically disables based on user permissions
 */

import React from 'react';
import { Button, Tooltip } from '@fluentui/react-components';
import { usePermissions } from '../../contexts/PermissionContext';

interface ProtectedButtonProps {
  privilege?: string;
  anyPrivilege?: string[];
  allPrivileges?: string[];
  hideIfNoAccess?: boolean;
  disabledTooltip?: string;
  disabled?: boolean;
  children?: React.ReactNode;
  appearance?: 'primary' | 'secondary' | 'subtle' | 'transparent';
  size?: 'small' | 'medium' | 'large';
  icon?: React.ReactElement;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

export const ProtectedButton: React.FC<ProtectedButtonProps> = ({
  privilege,
  anyPrivilege,
  allPrivileges,
  hideIfNoAccess = false,
  disabledTooltip = "You don't have permission to perform this action",
  disabled,
  children,
  appearance,
  size,
  icon,
  onClick,
  type,
  className,
}) => {
  const { hasPrivilege, hasAnyPrivilege, hasAllPrivileges, loading } = usePermissions();

  if (loading) {
    return null;
  }

  let hasPermission = true;

  if (privilege) {
    hasPermission = hasPrivilege(privilege);
  } else if (anyPrivilege && anyPrivilege.length > 0) {
    hasPermission = hasAnyPrivilege(anyPrivilege);
  } else if (allPrivileges && allPrivileges.length > 0) {
    hasPermission = hasAllPrivileges(allPrivileges);
  }

  if (!hasPermission && hideIfNoAccess) {
    return null;
  }

  const isDisabled = disabled || !hasPermission;

  if (!hasPermission && disabledTooltip) {
    return (
      <Tooltip content={disabledTooltip} relationship="label">
        <Button
          appearance={appearance}
          size={size}
          icon={icon}
          onClick={onClick}
          type={type}
          className={className}
          disabled={isDisabled}
        >
          {children}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button
      appearance={appearance}
      size={size}
      icon={icon}
      onClick={onClick}
      type={type}
      className={className}
      disabled={isDisabled}
    >
      {children}
    </Button>
  );
};
